/**
 * Renders a versioned legal document (./model.ts). Used by the public
 * `/legal/:doc` page, which OrgSetup, the terms modal and Settings link to.
 *
 * Only the Partner Terms are published. The DPA (./dpa.ts) is available on
 * request and must not be imported here or anywhere else in the app.
 */
import { Fragment, type ReactNode } from 'react';
import type { LegalBlock, LegalDocument } from './model';
import { PUBLISHED_LEGAL_DOCUMENTS, legalVersionLabel } from './current';

// Every published document (current.ts): the Partner Terms and the
// Contractor Fee Schedule. Never the on-request DPA.
export const LEGAL_DOCUMENTS: Record<string, LegalDocument> = { ...PUBLISHED_LEGAL_DOCUMENTS };

const EMAIL = /([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;

/**
 * Email addresses in the text become mailto links. Only the rendering
 * changes: the words are exactly the versioned text (never edit it here).
 */
export function linkify(text: string): ReactNode {
  const parts = text.split(EMAIL);
  if (parts.length === 1) return text;
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <a key={i} href={`mailto:${part.replace(/\.$/, '')}`}>
        {part}
      </a>
    ) : (
      <Fragment key={i}>{part}</Fragment>
    ),
  );
}

function Block({ block }: { block: LegalBlock }) {
  if (typeof block === 'string') return <p>{linkify(block)}</p>;
  if ('list' in block) {
    return (
      <ul className="stack-sm" style={{ paddingLeft: 18, margin: 0 }}>
        {block.list.map((item) => (
          <li key={item}>{linkify(item)}</li>
        ))}
      </ul>
    );
  }
  return (
    <div className="table-wrap">
      <table className="data legal-table">
        <thead>
          <tr>
            {block.table.head.map((h) => (
              <th key={h} scope="col">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.table.rows.map((row) => (
            <tr key={row.join('|')}>
              {row.map((cell, i) => (
                <td key={i}>{linkify(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * `toc`: a list of the sections (their stable ids are the anchors), for the
 * long public page; the terms modal leaves it off.
 */
export function LegalDocumentView({ doc, toc = false }: { doc: LegalDocument; toc?: boolean }) {
  return (
    <article className="legal stack" aria-labelledby="legal-title">
      <header className="stack-sm">
        <h1 id="legal-title">{doc.title}</h1>
        <p className="small muted">
          Version of {legalVersionLabel(doc.version)} · last updated {doc.updated}
        </p>
      </header>
      {toc && doc.sections.length > 3 ? (
        <nav className="legal-toc" aria-labelledby="legal-toc-h">
          <h2 id="legal-toc-h" className="h-sub">
            Contents
          </h2>
          <ol>
            {doc.sections.map((s) => (
              <li key={s.id}>
                <a href={`#${s.id}`}>{s.heading}</a>
              </li>
            ))}
          </ol>
        </nav>
      ) : null}
      {doc.intro.map((b, i) => (
        <Block key={i} block={b} />
      ))}
      {doc.sections.map((s) => (
        <section key={s.id} id={s.id} className="stack-sm" aria-labelledby={`${s.id}-h`}>
          <h2 id={`${s.id}-h`}>{s.heading}</h2>
          {s.blocks.map((b, i) => (
            <Block key={i} block={b} />
          ))}
        </section>
      ))}
    </article>
  );
}
